import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { adminAPI } from '../../lib/api';
import { formatCurrency } from '../../lib/utils';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Users, Crown, DollarSign, TrendingUp, Loader2, AlertTriangle, Clock, CreditCard, Headphones } from 'lucide-react';
import AdminPulseWidget from '../../components/admin/AdminPulseWidget';

export default function AdminDashboard() {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    loadDashboard();
  }, []);

  const loadDashboard = async () => {
    try {
      const response = await adminAPI.getDashboard();
      setStats(response.data);
    } catch (error) {
      console.error('Error loading dashboard:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  const breakdown = stats?.subscription_breakdown || {};

  const statCards = [
    {
      title: 'Total Landlords',
      value: stats?.total_landlords || 0,
      icon: Users,
      color: 'bg-indigo-500',
    },
    {
      title: 'Total Tenants',
      value: stats?.total_tenants || 0,
      icon: Users,
      color: 'bg-emerald-500',
    },
    {
      title: 'Paid Subscriptions',
      value: breakdown.active_paid || 0,
      icon: Crown,
      color: 'bg-amber-500',
    },
    {
      title: 'Subscription Revenue',
      value: formatCurrency(stats?.subscription_revenue || 0),
      icon: DollarSign,
      color: 'bg-[#C85A42]',
    },
  ];

  return (
    <div className="space-y-6" data-testid="admin-dashboard">
      <div>
        <h1 className="font-heading text-2xl lg:text-3xl font-bold">Admin Dashboard</h1>
        <p className="text-muted-foreground mt-1">Manage landlords and subscriptions</p>
      </div>

      {/* Business Pulse Widget */}
      <AdminPulseWidget />

      {/* Stats Grid */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        {statCards.map((stat, index) => (
          <Card key={stat.title} className="animate-fade-in" style={{ animationDelay: `${index * 0.05}s` }}>
            <CardContent className="p-6">
              <div className="flex items-start justify-between">
                <div>
                  <p className="text-sm text-muted-foreground">{stat.title}</p>
                  <p className="text-2xl font-bold mt-1">{stat.value}</p>
                </div>
                <div className={`w-10 h-10 rounded-lg ${stat.color} flex items-center justify-center`}>
                  <stat.icon className="w-5 h-5 text-white" />
                </div>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Subscription Breakdown + Alerts */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Subscription Breakdown */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <CreditCard className="w-5 h-5 text-[#C85A42]" />
              Subscription Breakdown
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              <div className="flex justify-between items-center py-2 border-b">
                <div className="flex items-center gap-2">
                  <div className="w-2.5 h-2.5 rounded-full bg-emerald-500" />
                  <span className="text-sm text-muted-foreground">Paid Active</span>
                </div>
                <Badge variant="secondary" className="font-semibold">{breakdown.active_paid || 0}</Badge>
              </div>
              <div className="flex justify-between items-center py-2 border-b">
                <div className="flex items-center gap-2">
                  <div className="w-2.5 h-2.5 rounded-full bg-blue-500" />
                  <span className="text-sm text-muted-foreground">Active Trials</span>
                </div>
                <Badge variant="secondary" className="font-semibold">{breakdown.active_trials || 0}</Badge>
              </div>
              <div className="flex justify-between items-center py-2 border-b">
                <div className="flex items-center gap-2">
                  <div className="w-2.5 h-2.5 rounded-full bg-amber-500" />
                  <span className="text-sm text-muted-foreground">Expired Trials</span>
                </div>
                <Badge variant="secondary" className="font-semibold text-amber-700">{breakdown.expired_trials || 0}</Badge>
              </div>
              <div className="flex justify-between items-center py-2">
                <div className="flex items-center gap-2">
                  <div className="w-2.5 h-2.5 rounded-full bg-red-500" />
                  <span className="text-sm text-muted-foreground">Expired Paid</span>
                </div>
                <Badge variant="secondary" className="font-semibold text-red-700">{breakdown.expired_paid || 0}</Badge>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Platform Overview */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <TrendingUp className="w-5 h-5 text-[#C85A42]" />
              Platform Overview
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              <div className="flex justify-between items-center py-2 border-b">
                <span className="text-sm text-muted-foreground">Active Users</span>
                <span className="font-semibold">{(stats?.total_landlords || 0) + (stats?.total_tenants || 0)}</span>
              </div>
              <div className="flex justify-between items-center py-2 border-b">
                <span className="text-sm text-muted-foreground">Subscription Rate</span>
                <span className="font-semibold">
                  {stats?.total_landlords > 0
                    ? Math.round(((breakdown.active_paid || 0) / stats?.total_landlords) * 100)
                    : 0}%
                </span>
              </div>
              <div className="flex justify-between items-center py-2">
                <span className="text-sm text-muted-foreground">Avg. Revenue / Landlord</span>
                <span className="font-semibold">
                  {formatCurrency(
                    stats?.total_landlords > 0
                      ? stats?.subscription_revenue / stats?.total_landlords
                      : 0
                  )}
                </span>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Alerts */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <AlertTriangle className="w-5 h-5 text-amber-500" />
              Attention Needed
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {(stats?.open_support_tickets || 0) > 0 && (
                <div 
                  className="flex items-center gap-3 p-3 rounded-lg bg-amber-50 border border-amber-200 cursor-pointer hover:bg-amber-100 transition-colors"
                  onClick={() => navigate('/admin/support')}
                  data-testid="alert-open-tickets"
                >
                  <Headphones className="w-5 h-5 text-amber-600 flex-shrink-0" />
                  <div>
                    <p className="text-sm font-medium text-amber-800">{stats.open_support_tickets} Open Support Ticket{stats.open_support_tickets > 1 ? 's' : ''}</p>
                    <p className="text-xs text-amber-600">Click to review</p>
                  </div>
                </div>
              )}
              {(breakdown.expired_trials || 0) > 0 && (
                <div 
                  className="flex items-center gap-3 p-3 rounded-lg bg-orange-50 border border-orange-200 cursor-pointer hover:bg-orange-100 transition-colors"
                  onClick={() => navigate('/admin/subscriptions')}
                  data-testid="alert-expired-trials"
                >
                  <Clock className="w-5 h-5 text-orange-600 flex-shrink-0" />
                  <div>
                    <p className="text-sm font-medium text-orange-800">{breakdown.expired_trials} Expired Trial{breakdown.expired_trials > 1 ? 's' : ''}</p>
                    <p className="text-xs text-orange-600">Landlords awaiting conversion</p>
                  </div>
                </div>
              )}
              {(stats?.open_support_tickets || 0) === 0 && (breakdown.expired_trials || 0) === 0 && (
                <div className="flex items-center gap-3 p-3 rounded-lg bg-emerald-50 border border-emerald-200">
                  <div className="w-5 h-5 rounded-full bg-emerald-500 flex items-center justify-center flex-shrink-0">
                    <svg className="w-3 h-3 text-white" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3"><path d="M20 6L9 17l-5-5"/></svg>
                  </div>
                  <p className="text-sm text-emerald-700">All clear - no urgent items</p>
                </div>
              )}
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
